<svelte:options customElement={{ tag: "bt-add-external-link-modal", shadow: "none" }} />

<script lang="ts">
  import Modal from '$shared/modal/Modal.svelte';
  import { modalStore } from '$shared/modal/modalStore';
  import { eventBus } from '$shared/lib/eventBus';
  import { onMount, onDestroy } from 'svelte';

  export let addModalText: string;
  export let addToCurationText: string;
  export let contentTypeText: string;
  export let curationId: number;
  export let descriptionLabel: string;
  export let dropdownText: string;
  export let dropdownArticle: string;
  export let dropdownAudio: string;
  export let dropdownLink: string;
  export let dropdownSurvey: string;
  export let editModalText: string;
  export let estimatedDurationLabel: string;
  export let dropdownVideo: string;
  export let linkText: string;
  export let modalTitle: string = "Add external link";
  export let optionalText: string;
  export let pasteUrlHereText: string;
  export let requiredText: string;
  export let saveChangesText: string;
  export let titleLabel: string;
  export let titlePlaceholder: string;
  export let validUrlText: string;

  let mode: 'POST' | 'PATCH' = 'POST'
  let externalLinkId: number | null = null;
  let curatedItemId: number | null = null;
  
  let url: string = "";
  let title: string = "";
  let description: string = "";
  let contentType: string = "";
  let duration: string = ""
  let isValidUrl = false;

  const knownMaliciousPatterns = [
    /[<>]/,                      // raw angle brackets (autolinks/HTML snippets)
    /<script.*?>.*?<\/script>/i, // inline script tags
    /%3C\s*script\b/i,           // encoded "<script"
    /['"`;]/i,                   // potential script or SQL injection characters
    /\b(?:UNION(?:\s+ALL)?\s+SELECT|SELECT|INSERT|DELETE|DROP|UPDATE|ALTER|TRUNCATE)\b/i, // SQL keywords
  ];

  const hasMaliciousPatterns = (value: string): boolean => {
    return knownMaliciousPatterns.some(pattern => pattern.test(value));
  }

  const validateUrl = (value: string): boolean => {
    try {
      const url = new URL(value);
      if (!['http:', 'https:'].includes(url.protocol)) return false;
      if (/[\s]/.test(value)) return false;// block raw whitespace
      if (hasMaliciousPatterns(value)) return false;
      if (value.length > 2048) return false;

      return true;
    } catch {
      return false
    }
  };

  $: isValidUrl = validateUrl(url);
  $: isPlaceholderSelected = contentType === "";
  $: modalTitle = mode === 'PATCH' ? editModalText : addModalText;

  const resetForm = () => {
    url = "";
    title = "";
    description = "";
    contentType = "";
    duration = "";
  };

  const prefill = async (curation_id: number, link_id: number) => {
    try {
      const res = await fetch(`/curations/${curation_id}/external_links/${link_id}.json`, {
        headers: { "Accept": "application/json" },
        credentials: "same-origin",
      });
      if (!res.ok) throw new Error("Failed to load link");
      const data = await res.json();

      title = data.title || "";
      url = data.url || "";
      description = data.description || "";
      contentType = data.content_type || "Link";
      duration = data.duration ? String(Math.floor(Number(data.duration) / 60)) : "";
    } catch (error) {
      console.error(error);
    }
  }

  const onCreateOpen = (e: Event) => {
    const data = (e as CustomEvent).detail || {};
    mode = 'POST';
    curationId = data.curationId ?? curationId;
    externalLinkId = null;
    curatedItemId = null;
    resetForm();
  }

  const onEditOpen = async (event: any) => {
    const data = (event).detail || {};
    mode = 'PATCH';
    curationId = data.curationId ?? curationId;
    externalLinkId = data.externalLinkId ?? null;
    curatedItemId = data.curatedItemId ?? null;
    resetForm();
    if (curationId && externalLinkId) await prefill(curationId, externalLinkId);
  }

  onMount(() => {
    document.addEventListener('external-link-opened', onCreateOpen);
    document.addEventListener('external-link-edit-opened', onEditOpen);
  });

  onDestroy(() => {
    document.removeEventListener('external-link-opened', onCreateOpen);
    document.removeEventListener('external-link-edit-opened', onEditOpen);
  });

  const handleSubmit = async () => {
    const payload = {
      url: url,
      title: title,
      description: description,
      content_type: contentType || "Link",
      duration: Number(duration) ? Number(duration) * 60 : 0,
      curation_id: curationId,
    };

    const isEdit = mode === 'PATCH';
    const endpoint = isEdit ? `/curations/${payload.curation_id}/external_links/${externalLinkId}` : `/curations/${payload.curation_id}/create_external_link`;

    try {
      const response = await fetch(endpoint, {
        method: mode,
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
        },
        body: JSON.stringify({ external_link: payload }),
        credentials: "same-origin",
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        console.error(`${isEdit ? 'Error updating' : 'Error creating'} external link:`, error);
        alert(`Failed to ${isEdit ? 'update' : 'create'} external link. Please try again.`);
        return;
      }

      const data = await response.json();
      let updated;

      // Prefer the curated_item payload, otherwise use the bare external_link id as curatable_id Fall back is data.
      if (data.curated_item) {
          updated = data.curated_item;
          } else if (data.external_link) {
            updated = { ...data.external_link, curatable_id: data.external_link.id };
          } else {
            updated = data;
          }

      eventBus.emit(isEdit ? 'external-link-updated' : 'external-link-added', updated);

      modalStore.setName("");
      modalStore.setData({});
      resetForm();
      mode = 'POST';
      externalLinkId = null;
      curatedItemId = null;

    } catch (error){
      console.log("Error submitting external link:", error);
      alert("Save failed. Please try again.");
    }
  }
</script>

{#if $modalStore.modalName === 'externalLinkModal'}
  <Modal modalTitle={mode === 'PATCH' ? editModalText : addModalText} width="wide">
    <div slot="modal-contents" class="space-y-6 p-6 max-sm:px-0 text-neutral-800 text-sm">
      <!-- Link -->
      <div>
        <label for="url" class="block font-medium">{linkText}</label>
        <p class="text-xs text-neutral-500 mt-0.5">{requiredText}</p>
        <input id="url" type="text" bind:value={url} placeholder={pasteUrlHereText}
        class={`w-full rounded-md px-4 py-2 mt-1 ${url && !isValidUrl ? 'border-red focus:ring-red focus:border-red' : ''}`}
        required />
        {#if url && !isValidUrl}
          <p class="text-xs text-red mt-1">{validUrlText}</p>
        {/if}
      </div>
      <!-- Title -->
      <div>
        <label for="title" class="block font-medium">{titleLabel}</label>
        <p class="text-xs text-neutral-500 mt-0.5">{requiredText}</p>
        <input id="title" type="text" bind:value={title} placeholder={titlePlaceholder} class="w-full border border-neutral-300 rounded-md px-4 py-2 mt-1" required />
      </div>
      <!-- Description -->
      <div>
        <label for="description" class="block font-medium">{descriptionLabel}</label>
        <p class="text-xs text-neutral-500 mt-0.5">{optionalText}</p>
        <input id="description" type="text" bind:value={description} placeholder={titlePlaceholder} class="w-full border border-neutral-300 rounded-md px-4 py-2 mt-1" />
      </div>
      <!-- Content Type -->
      <div>
        <label for="contentType" class="block font-medium">{contentTypeText}</label>
        <p class="text-xs text-neutral-500 mt-0.5">{optionalText}</p>
        <select id="contentType" bind:value={contentType} class={`w-full border border-neutral-300 rounded-md px-4 py-2 mt-1 ${isPlaceholderSelected ? 'text-neutral-500' : 'text-neutral-800'}`}>
          <option value="" disabled>{dropdownText}</option>
          <option value="Link">{dropdownLink}</option>
          <option value="Article">{dropdownArticle}</option>
          <option value="Video">{dropdownVideo}</option>
          <option value="Audio">{dropdownAudio}</option>
          <option value="Survey/Poll">{dropdownSurvey}</option>
        </select>
      </div>
      <!-- Duration -->
      <div>
        <label for="duration" class="block font-medium">{estimatedDurationLabel}</label>
        <p class="text-xs text-neutral-500 mt-0.5">{optionalText}</p>
        <input id="duration" type="number"  bind:value={duration} placeholder="0" class="w-full border border-neutral-300 rounded-md px-4 py-2 mt-1" />
      </div>
    </div>
    <!-- Buttons -->
    <div slot="modal-control-bar" class="flex justify-end gap-4">
      <button
        class="text-sm font-medium bg-neutral-800 text-white px-4 py-2 rounded disabled:opacity-50"
        disabled={!url || !title || !isValidUrl}
        on:click={handleSubmit}
      >
        {mode === 'PATCH' ? saveChangesText : addToCurationText}
      </button>
    </div>
  </Modal>
{/if}